// Вкладка «Обзор» админ-консоли: системная статистика, RAG-метрики, Semantic Graph,
// состояние горячего хранилища видео.
// Выделено из admin.ts (аудит, этап 2 — декомпозиция).
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';

/**
 * Состояние горячего хранилища видео (AV-ADR-1, AV-HC-5).
 *
 * Экран СОСТОЯНИЯ, а не настройки: параметры кеша живут в env (решение
 * владельца 2026-08-07), поэтому полей ввода здесь нет. Зато у каждого
 * значения показан источник — env сервера или умолчание: экран реквизитов
 * Диска уже обжигался на том, что показывал не действующее значение.
 */
interface AvgVideoCacheState {
  disabled: boolean;
  disabled_source: 'env' | 'default';
  dir: string | null;
  dir_source: 'env' | 'default';
  dir_conflict: boolean;
  files: number;
  bytes: number;
  limit_bytes: number;
  limit_source: 'env' | 'default';
  used_percent: number;
  ttl_days: number;
  ttl_source: 'env' | 'default';
  oldest_at: string | null;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} ГБ`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} МБ`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${bytes} Б`;
}

function sourceNote(source: 'env' | 'default'): string {
  return source === 'env' ? 'из env сервера' : 'умолчание';
}

function infoRow(label: string, value: string): string {
  return `<div class="system-info-item"><span class="system-info-label">${DOMPurify.sanitize(label)}</span>`
    + `<span class="system-info-value">${DOMPurify.sanitize(value)}</span></div>`;
}

function renderAvgVideoCacheState(cache: AvgVideoCacheState): void {
  const panel = $('avg-video-cache-panel');
  if (!panel) return;

  const rows: string[] = [];
  if (cache.dir_conflict) {
    // Настройка задевает каталог «Эфира» — кеш выключен принудительно, иначе
    // его подметание съело бы чужие файлы (AV-ADR-1).
    rows.push(infoRow('Состояние', 'выключено: каталог задевает хранилище «Эфира»'));
  } else if (cache.disabled) {
    rows.push(infoRow('Состояние', 'выключено (AVG_VIDEO_CACHE_DISABLED)'));
  } else {
    rows.push(infoRow('Состояние', 'включено'));
  }

  rows.push(infoRow(
    'Занято из квоты',
    `${formatBytes(cache.bytes)} из ${formatBytes(cache.limit_bytes)} (${cache.used_percent}%, ${sourceNote(cache.limit_source)})`
  ));
  rows.push(infoRow('Файлов', String(cache.files)));
  rows.push(infoRow(
    'Самый старый',
    cache.oldest_at ? new Date(cache.oldest_at).toLocaleString('ru-RU') : '—'
  ));
  rows.push(infoRow('Срок невостребованности', `${cache.ttl_days} дн. (${sourceNote(cache.ttl_source)})`));
  rows.push(infoRow('Каталог', `${cache.dir || '—'} (${sourceNote(cache.dir_source)})`));

  panel.innerHTML = rows.join('');
}

export async function loadAvgVideoCacheState(): Promise<void> {
  const panel = $('avg-video-cache-panel');
  if (!panel) return;
  try {
    const r = await fetch('/api/admin/avg-video/cache', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) throw new Error('Не удалось получить состояние горячего хранилища');
    renderAvgVideoCacheState(await r.json() as AvgVideoCacheState);
  } catch (e) {
    panel.innerHTML = '<div class="system-info-item"><span class="system-info-label">Состояние недоступно</span></div>';
    console.error('AvG Video cache state failed', e);
  }
}

/** Кнопка «Очистить»: файлы есть на Диске, поэтому чистка ничего не теряет. */
export async function clearAvgVideoCacheState(): Promise<void> {
  if (!confirm('Очистить горячее хранилище видео? Файлы останутся на Яндекс.Диске, кеш наполнится заново при просмотрах.')) return;
  try {
    const r = await fetch('/api/admin/avg-video/cache/clear', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) throw new Error('Не удалось очистить горячее хранилище');
    const result = await r.json() as AvgVideoCacheState & { removed: number };
    renderAvgVideoCacheState(result);
    showToast(`✅ Горячее хранилище очищено (файлов: ${result.removed})`);
  } catch (e) {
    showToast('❌ ' + (e instanceof Error ? e.message : String(e)));
  }
}

export async function loadAdminStats() {
  // Отдельным запросом и без await: состояние кеша не должно ни ждать
  // системную статистику, ни пропадать вместе с её ошибкой.
  void loadAvgVideoCacheState();
  try {
    const headers = { 'Authorization': 'Bearer ' + state.authToken || '' };
    const [statsRes, mvpRes, graphRes] = await Promise.all([
      fetch('/api/admin/stats', { headers }),
      fetch('/api/admin/dashboard/mvp', { headers }),
      fetch('/api/admin/semantic-graph/stats', { headers }),
    ]);
    if (!statsRes.ok) return;
    const stats = await statsRes.json() as {
      users: { total: number; expired: number };
      sessions: { total: number };
      categories: number;
      system: {
        uptime: number;
        memory: { rss: number };
        platform: string;
        node_version: string;
        os_free_mem: number;
        os_load: number[];
      };
    };
    const mvp = mvpRes.ok ? await mvpRes.json() as {
      rag_metrics?: {
        rag_latency_ms?: { p95?: number };
        degraded_rate?: number;
        degraded_local_rate?: number;
        degraded_remote_rate?: number;
        retrieval_count?: number;
        remote_retrieval_count?: number;
        remote_vendor_calls?: number;
      };
      semantic_quality_score?: number;
      feature_flags?: { RAG_V2_ENABLED?: boolean };
    } | null : null;

    if ($('stat-total-users')) $('stat-total-users')!.textContent = String(stats.users.total);
    if ($('stat-expired-users')) $('stat-expired-users')!.textContent = `${stats.users.expired} истекло`;
    if ($('stat-total-sessions')) $('stat-total-sessions')!.textContent = String(stats.sessions.total);
    if ($('stat-total-categories')) $('stat-total-categories')!.textContent = String(stats.categories);

    const uptimeS = stats.system.uptime;
    const h = Math.floor(uptimeS / 3600);
    const m = Math.floor((uptimeS % 3600) / 60);
    const s = uptimeS % 60;
    if ($('stat-uptime')) $('stat-uptime')!.textContent = `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;

    const memMb = Math.round(stats.system.memory.rss / 1024 / 1024);
    if ($('stat-memory')) $('stat-memory')!.textContent = `${memMb} MB RAM`;

    const ragMetrics = mvp?.rag_metrics;
    const ragP95 = ragMetrics?.rag_latency_ms?.p95;
    if ($('stat-rag-latency-p95')) {
      $('stat-rag-latency-p95')!.textContent = ragP95 != null && ragP95 > 0
        ? `${Math.round(ragP95)} ms`
        : '—';
    }
    const degradedRate = ragMetrics?.degraded_rate;
    if ($('stat-rag-degraded')) {
      // Общая доля осталась главной цифрой, но рядом стоит разбор по причинам:
      // «наш» лечится реранкером и индексом, «вендор» — вендором и таймаутом.
      const pct = (value: number) => `${(value * 100).toFixed(1)}%`;
      const localRate = ragMetrics?.degraded_local_rate;
      const remoteRate = ragMetrics?.degraded_remote_rate;
      const split = localRate != null && remoteRate != null
        ? ` (наш ${pct(localRate)} · вендор ${pct(remoteRate)})`
        : '';
      $('stat-rag-degraded')!.textContent = degradedRate != null
        ? `degraded ${pct(degradedRate)}${split}`
        : 'degraded —';
    }

    // В-3: оплачиваемые вызовы вендорам хранилищ. Это деньги — их место в
    // сводке, а не в трассировке хода, откуда их доставали грепом.
    const vendorCalls = ragMetrics?.remote_vendor_calls;
    if ($('stat-remote-vendor-calls')) {
      $('stat-remote-vendor-calls')!.textContent = vendorCalls != null ? String(vendorCalls) : '—';
    }
    const remoteRetrievals = ragMetrics?.remote_retrieval_count;
    if ($('stat-remote-retrievals')) {
      $('stat-remote-retrievals')!.textContent = remoteRetrievals != null
        ? `ходов с внешним поиском ${remoteRetrievals}`
        : 'ходов с внешним поиском —';
    }

    const semanticScore = mvp?.semantic_quality_score;
    if ($('stat-semantic-quality')) {
      $('stat-semantic-quality')!.textContent = semanticScore != null
        ? semanticScore.toFixed(3)
        : '—';
    }
    const retrievalCount = ragMetrics?.retrieval_count ?? 0;
    if ($('stat-rag-retrievals')) $('stat-rag-retrievals')!.textContent = `retrievals ${retrievalCount}`;

    if (graphRes.ok) {
      const graph = await graphRes.json() as {
        nodeCount?: number;
        edgeCount?: number;
        chunksWithEntities?: number;
        namespace?: string;
        edgeTypes?: Record<string, number>;
        config?: { entityExtractionMode?: string; graphExpandMaxHops?: number };
      };
      if ($('stat-graph-nodes')) {
        $('stat-graph-nodes')!.textContent = graph.nodeCount != null ? String(graph.nodeCount) : '—';
      }
      if ($('stat-graph-chunks')) {
        const chunks = graph.chunksWithEntities ?? 0;
        const edges = graph.edgeCount ?? 0;
        $('stat-graph-chunks')!.textContent = `${chunks} chunks · ${edges} edges`;
      }
      const panel = $('semantic-graph-admin-panel');
      if (panel) {
        const edgeTypes = Object.entries(graph.edgeTypes || {})
          .map(([type, count]) => `${type}: ${count}`)
          .join(', ') || '—';
        panel.innerHTML = `
          <div class="system-info-item"><span class="system-info-label">Namespace</span><span class="system-info-value">${DOMPurify.sanitize(graph.namespace || '—')}</span></div>
          <div class="system-info-item"><span class="system-info-label">Nodes / edges</span><span class="system-info-value">${graph.nodeCount ?? 0} / ${graph.edgeCount ?? 0}</span></div>
          <div class="system-info-item"><span class="system-info-label">Chunks с entity_ids</span><span class="system-info-value">${graph.chunksWithEntities ?? 0}</span></div>
          <div class="system-info-item"><span class="system-info-label">Edge types</span><span class="system-info-value">${DOMPurify.sanitize(edgeTypes)}</span></div>
          <div class="system-info-item"><span class="system-info-label">Extraction mode</span><span class="system-info-value">${DOMPurify.sanitize(graph.config?.entityExtractionMode || 'rule')}</span></div>
          <div class="system-info-item"><span class="system-info-label">Max hops</span><span class="system-info-value">${graph.config?.graphExpandMaxHops ?? 1}</span></div>
        `;
      }
    }

    const list = $('system-info-list');
    if (list) {
      list.innerHTML = `
      <div class="system-info-item"><span class="system-info-label">Платформа</span><span class="system-info-value">${DOMPurify.sanitize(stats.system.platform)}</span></div>
      <div class="system-info-item"><span class="system-info-label">Node.js</span><span class="system-info-value">${DOMPurify.sanitize(stats.system.node_version)}</span></div>
      <div class="system-info-item"><span class="system-info-label">Свободная память</span><span class="system-info-value">${Math.round(stats.system.os_free_mem / 1024 / 1024)} MB</span></div>
      <div class="system-info-item"><span class="system-info-label">Загрузка (1/5/15)</span><span class="system-info-value">${DOMPurify.sanitize(stats.system.os_load.map(l => l.toFixed(2)).join(' / '))}</span></div>
      ${mvp?.feature_flags?.RAG_V2_ENABLED != null ? `<div class="system-info-item"><span class="system-info-label">База знаний v2</span><span class="system-info-value">${mvp.feature_flags.RAG_V2_ENABLED ? 'enabled' : 'disabled'}</span></div>` : ''}
    `;
    }
  } catch (e) { console.error('Stats load failed', e); }
}
